import React, { useState, useEffect } from 'react';
import { format, subMonths, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Reservation } from '@/types/supabase';
import { Contract } from '@/types/contracts';
import { getContractsByReservation } from '@/services/contractService';
import { usePaymentsByReservation, useCreatePaymentRecords } from '@/hooks/usePayments';
import { Button } from '@/components/ui/button';
import { FileText, Clock, Check, Euro, Calendar, Shield } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import PaymentTimeline from '@/components/payments/PaymentTimeline';
import PaymentButton from '@/components/payments/PaymentButton';
import PaymentStatus from '@/components/payments/PaymentStatus';
import { supabase } from '@/integrations/supabase/client';
import { getCheckInByReservationId } from '@/services/checkInService';

interface ReservationTimelineProps {
  reservation: Reservation;
}

interface TimelineStep {
  id: string;
  title: string;
  description: string;
  status: 'completed' | 'pending' | 'upcoming';
  date?: string;
  action?: {
    label: string;
    url: string;
    disabled?: boolean;
  };
  payment?: any;
  icon: React.ReactNode;
}

const ReservationTimeline: React.FC<ReservationTimelineProps> = ({ reservation }) => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [paymentsInitialized, setPaymentsInitialized] = useState(false);
  const [checkInToken, setCheckInToken] = useState<string | null>(null);
  const [checkInCompleted, setCheckInCompleted] = useState(false);
  
  // Vérifier si les paiements/contrats doivent être désactivés
  const canProcessPayments = reservation.source === 'Direct' || reservation.source === 'PAP';

  // Récupération des paiements
  const { data: payments = [], isLoading: paymentsLoading, refetch: refetchPayments } = usePaymentsByReservation(reservation.id);
  const createPaymentRecordsMutation = useCreatePaymentRecords();

  console.log('Payments for reservation:', reservation.id, payments);

  useEffect(() => {
    const fetchContracts = async () => {
      try {
        const contractsData = await getContractsByReservation(reservation.id);
        setContracts(contractsData);
      } catch (error) {
        console.error('Erreur lors du chargement des contrats:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchCheckIn = async () => {
      try {
        const checkIn = await getCheckInByReservationId(reservation.id);
        if (checkIn) {
          setCheckInToken(checkIn.unique_token || null);
          setCheckInCompleted(!!checkIn.completed_at);
        }
      } catch (error) {
        console.error('Erreur lors du chargement du check-in:', error);
      }
    };

    if (reservation.status === 'confirmed') {
      fetchContracts();
      fetchCheckIn();
    } else {
      setLoading(false);
    }
  }, [reservation.id, reservation.status]);

  // Créer les enregistrements de paiement s'ils n'existent pas (une seule fois) - seulement pour Direct et PAP
  useEffect(() => {
    if (canProcessPayments &&
        reservation.status === 'confirmed' && 
        !paymentsLoading && 
        payments.length === 0 && 
        !paymentsInitialized && 
        !createPaymentRecordsMutation.isPending) {
      
      console.log('Créer les paiements pour la réservation:', reservation.id);
      setPaymentsInitialized(true); // Marquer comme initialisé immédiatement pour éviter les doublons
      
      createPaymentRecordsMutation.mutate(reservation.id, {
        onSuccess: () => {
          console.log('Paiements créés avec succès, rechargement...');
          refetchPayments();
        },
        onError: (error) => {
          console.error('Erreur lors de la création des paiements:', error);
          // En cas d'erreur, permettre une nouvelle tentative
          setPaymentsInitialized(false);
        }
      });
    } else if (payments.length > 0) {
      setPaymentsInitialized(true);
    }
  }, [canProcessPayments, reservation.status, paymentsLoading, payments.length, reservation.id, paymentsInitialized, createPaymentRecordsMutation, refetchPayments]);

  const getContractStatus = (contractType: 'lease' | 'rules') => {
    const contract = contracts.find(c => c.contract_type === contractType);
    if (!contract) return 'upcoming';
    return contract.status === 'signed' ? 'completed' : 'pending';
  };

  const getContractAction = (contractType: 'lease' | 'rules') => {
    const contract = contracts.find(c => c.contract_type === contractType);
    if (!contract) return undefined;
    
    return {
      label: contract.status === 'signed' ? 'Voir le document' : 'Signer maintenant',
      url: `/sign-contract/${contract.unique_token}`,
      disabled: false
    };
  };

  const getSteps = (): TimelineStep[] => {
    const baseSteps: TimelineStep[] = [];

    // Étape 0: Guide de séjour (toujours présent)
    baseSteps.push({
      id: 'guide',
      title: 'Guide de séjour',
      description: 'Consultez toutes les informations pour votre séjour (WiFi, accès, contacts...)',
      status: 'completed',
      action: {
        label: 'Consulter le guide',
        url: `/reservation-directory/${reservation.id}`,
        disabled: false
      },
      icon: <Calendar className="h-5 w-5 text-blue-600" />
    });

    const isConfirmed = reservation.status === 'confirmed';
    
    // Si la source est AirBnb ou Booking.com, afficher un message différent
    if (!canProcessPayments) {
      baseSteps.push({
        id: 'external-booking',
        title: `Réservation via ${reservation.source}`,
        description: `Cette réservation a été effectuée via ${reservation.source}. Les paiements et contrats sont gérés par cette plateforme.`,
        status: isConfirmed ? 'completed' : 'pending',
        icon: <Check className="h-4 w-4" />
      });
      
      if (isConfirmed) {
        baseSteps.push({
          id: 'checkin',
          title: 'Arrivée à la villa',
          description: `Check-in prévu à partir de 17h`,
          status: 'upcoming',
          date: format(new Date(reservation.check_in), 'd MMMM yyyy', { locale: fr }),
          icon: <Calendar className="h-4 w-4" />
        });
      }
      
      return baseSteps;
    }

    if (isConfirmed) {
      // Étape 1: Contrat de location
      baseSteps.push({
        id: 'lease-contract',
        title: 'Signature du contrat de location',
        description: 'Contrat de location saisonnière à signer',
        status: getContractStatus('lease'),
        icon: <FileText className="h-4 w-4" />,
        action: getContractAction('lease')
      });

      // Étape 2: Règlement intérieur
      baseSteps.push({
        id: 'rules-contract',
        title: 'Signature du règlement intérieur',
        description: 'Règlement intérieur de la villa à accepter',
        status: getContractStatus('rules'),
        icon: <FileText className="h-4 w-4" />,
        action: getContractAction('rules')
      });

      // Étapes de paiement - utiliser les vrais paiements de la base de données
      if (payments.length > 0) {
        const sortedPayments = [...payments].sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime());
        
        // Filtrer pour éviter les doublons - garder seulement les paiements uniques par type
        const uniquePayments = sortedPayments.filter((payment, index, self) => {
          // Pour les paiements de type deposit_guarantee, garder tous
          if (payment.payment_type === 'deposit_guarantee') {
            return true;
          }
          // Pour les autres types, garder seulement le premier de chaque type
          return self.findIndex(p => p.payment_type === payment.payment_type) === index;
        });
        
        uniquePayments.forEach((payment) => {
          const paymentStatus = payment.status === 'paid' ? 'completed' : 
                               payment.status === 'failed' ? 'upcoming' : 'pending';
          
          const getPaymentTitle = () => {
            switch (payment.payment_type) {
              case 'deposit': return `Paiement de l'acompte (${payment.amount}€)`;
              case 'final': return `Paiement du solde (${payment.amount}€)`;
              case 'full': return `Paiement intégral (${payment.amount}€)`;
              case 'deposit_guarantee': return `Caution bancaire (${payment.amount}€)`;
              default: return `Paiement (${payment.amount}€)`;
            }
          };

          const getPaymentDescription = () => {
            if (payment.payment_type === 'deposit_guarantee') {
              return 'Autorisation bancaire de 30 jours - Non encaissée automatiquement';
            }
            const percentage = payment.payment_type === 'deposit' ? '30%' : 
                             payment.payment_type === 'final' ? '70%' : '100%';
            return `${percentage} du prix total${payment.payment_type === 'final' ? 
              ` à payer avant le ${format(new Date(payment.due_date), 'd MMMM yyyy', { locale: fr })}` : ''}`;
          };

          const getPaymentIcon = () => {
            return payment.payment_type === 'deposit_guarantee' ? 
              <Shield className="h-4 w-4" /> : 
              <Euro className="h-4 w-4" />;
          };

          baseSteps.push({
            id: `payment-${payment.id}`,
            title: getPaymentTitle(),
            description: getPaymentDescription(),
            status: paymentStatus,
            date: format(new Date(payment.due_date), 'd MMMM yyyy', { locale: fr }),
            icon: getPaymentIcon(),
            payment: payment
          });
        });
      }

      // Toujours afficher la caution bancaire pour les réservations confirmées si elle n'existe pas dans les paiements
      const daysUntilCheckin = differenceInDays(new Date(reservation.check_in), new Date());
      const hasDepositGuarantee = payments.some(p => p.payment_type === 'deposit_guarantee');
      
      // Afficher la caution si elle n'existe pas encore dans les paiements ET qu'on est à moins de 7 jours du séjour
      if (!hasDepositGuarantee && daysUntilCheckin <= 7) {
        baseSteps.push({
          id: 'deposit-guarantee-manual',
          title: 'Caution bancaire (2000€)',
          description: 'Autorisation bancaire de 30 jours - Non encaissée automatiquement',
          status: 'pending',
          date: format(new Date(reservation.check_in), 'd MMMM yyyy', { locale: fr }),
          icon: <Shield className="h-4 w-4" />
        });
      }

      // Étape de pré-check-in (visible uniquement 30 jours avant l'arrivée)
      if (daysUntilCheckin <= 30 && checkInToken) {
        const checkInAction = checkInCompleted ? undefined : {
          label: 'Compléter le check-in',
          url: `/check-in/${checkInToken}`,
          disabled: false
        };

        baseSteps.push({
          id: 'pre-checkin',
          title: 'Pré-Check-in',
          description: checkInCompleted 
            ? 'Check-in complété avec succès' 
            : 'Complétez vos informations de séjour en ligne',
          status: checkInCompleted ? 'completed' : 'pending',
          date: format(new Date(reservation.check_in), 'd MMMM yyyy', { locale: fr }),
          icon: <FileText className="h-4 w-4" />,
          action: checkInAction
        });
      }

      // Étape finale: Arrivée
      baseSteps.push({
        id: 'checkin',
        title: 'Arrivée à la villa',
        description: `Check-in prévu à partir de 17h`,
        status: 'upcoming',
        date: format(new Date(reservation.check_in), 'd MMMM yyyy', { locale: fr }),
        icon: <Calendar className="h-4 w-4" />
      });
    } else {
      // Pour les réservations non confirmées
      const getStatusForNonConfirmed = () => {
        if (reservation.status === 'cancelled') return 'upcoming';
        return reservation.status === 'new' ? 'pending' : 'pending';
      };

      baseSteps.push({
        id: 'confirmation',
        title: 'Confirmation de réservation',
        description: 'En attente de confirmation de la réservation',
        status: getStatusForNonConfirmed(),
        icon: <Clock className="h-4 w-4" />
      });
    }

    return baseSteps;
  };

  const steps = getSteps();

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">Terminé</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">En cours</Badge>;
      default:
        return <Badge variant="outline">À venir</Badge>;
    }
  };

  if (loading || paymentsLoading || createPaymentRecordsMutation.isPending) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-villa-blue">Étapes de votre réservation</h3>
        <div className="animate-pulse space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-villa-blue">Étapes de votre réservation</h3>
      
      <div className="space-y-4">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-start space-x-4">
            {/* Timeline line */}
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                step.status === 'completed' 
                  ? 'bg-green-100 border-green-500' 
                  : step.status === 'pending'
                  ? 'bg-yellow-100 border-yellow-500'
                  : 'bg-gray-100 border-gray-300'
              }`}>
                {step.status === 'completed' ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <div className={`text-sm ${
                    step.status === 'pending' ? 'text-yellow-600' : 'text-gray-400'
                  }`}>
                    {step.icon}
                  </div>
                )}
              </div>
              {index < steps.length - 1 && (
                <div className="w-0.5 h-8 bg-gray-200 mt-2"></div>
              )}
            </div>
            
            {/* Content */}
            <div className="flex-1 pb-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">{step.title}</h4>
                {getStatusBadge(step.status)}
              </div>
              
              <p className="text-sm text-gray-600 mb-2">{step.description}</p>
              
              {step.date && (
                <p className="text-xs text-gray-500 mb-2">
                  Date prévue: {step.date}
                </p>
              )}
              
              {/* Bouton d'action pour les contrats */}
              {step.action && (
                <Button
                  size="sm"
                  variant={step.status === 'completed' ? 'outline' : 'default'}
                  onClick={() => window.open(step.action!.url, '_blank')}
                  disabled={step.action.disabled}
                  className="mt-2"
                >
                  {step.action.label}
                </Button>
              )}

              {/* Affichage du statut et bouton de paiement */}
              {step.payment && (
                <div className="mt-3 space-y-2">
                  <PaymentStatus payment={step.payment} />
                  {step.payment.status === 'pending' && (
                    <PaymentButton payment={step.payment} />
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReservationTimeline;
